import React from 'react';
import Swal from 'sweetalert2';
import {
  STUDENT_STATUS_OPTIONS,
  computeBatchStatusCounts,
  normalizedDeliveryStatus,
  buildStudentExportRows,
  downloadStudentStatusTemplate,
} from '../utils/batchStudentStatusHelpers';

function formatScheduleType(scheduleType) {
  if (!scheduleType) return '';
  return scheduleType.charAt(0).toUpperCase() + scheduleType.slice(1);
}

function notifyNoStudents() {
  Swal.fire('Nothing to export', 'There are no students in this selection.', 'info');
}

function DownloadIconButton({ onClick, title }) {
  return (
    <button
      type="button"
      onClick={onClick}
      title={title}
      className="inline-flex h-4 w-4 shrink-0 items-center justify-center rounded text-gray-400 hover:bg-emerald-50 hover:text-emerald-600"
    >
      <svg xmlns="http://www.w3.org/2000/svg" className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
      </svg>
    </button>
  );
}

// Formal per-course "batch x status" headcount table — Batches / Total
// Students / one column per delivery-status — shared by the interactive
// report and its print view so both render an identical layout.
//
// When `downloadMode` is on, every header cell and every body cell grows a
// small download button that exports the matching student(s) as an .xlsx
// sheet in the same layout the existing /admin/students/pathway-stream-upload
// page expects, so it can be filled in and re-uploaded to bulk-update
// registrations.deliveryStatus / registrations.stream.
export default function BatchStatusSummaryTable({ course, downloadMode = false }) {
  const exportCourseColumn = (statusValue) => {
    const entries = [];
    course.batches.forEach((batch) => {
      batch.students.forEach((student) => {
        if (statusValue && normalizedDeliveryStatus(student.deliveryStatus) !== statusValue) return;
        entries.push({ student, batch });
      });
    });
    const rows = buildStudentExportRows(entries);
    if (!rows.length) {
      notifyNoStudents();
      return;
    }
    const courseLabel = course.courseShortName || course.courseName || 'Course';
    downloadStudentStatusTemplate(rows, `${courseLabel}_${statusValue || 'All'}`);
  };

  const exportBatchCell = (batch, statusValue) => {
    const entries = batch.students
      .filter((s) => !statusValue || normalizedDeliveryStatus(s.deliveryStatus) === statusValue)
      .map((student) => ({ student, batch }));
    const rows = buildStudentExportRows(entries);
    if (!rows.length) {
      notifyNoStudents();
      return;
    }
    const batchLabel = batch.batchInternalName || batch.whatsappGroupName || batch.batchName || 'Batch';
    downloadStudentStatusTemplate(rows, `${batchLabel}_${statusValue || 'All'}`);
  };

  return (
    <div>
      {/* Course name sits above the scrollable table (not inside it) so it
          stays put even when the wide status-breakdown table scrolls horizontally. */}
      <h3 className="mb-2 text-base font-bold text-gray-900">
        {course.courseShortName || course.courseName || 'Unknown Course'}
        {formatScheduleType(course.scheduleType) && ` (${formatScheduleType(course.scheduleType)})`}
      </h3>
      <div className="overflow-x-auto">
        <table className="w-full min-w-[900px] border-collapse text-xs">
          <thead>
            <tr className="bg-gray-100">
              <th className="border border-gray-400 px-2 py-1.5 text-left font-semibold text-gray-800">Batches</th>
              <th className="border border-gray-400 px-2 py-1.5 text-center font-semibold text-gray-800">
                <div className="flex items-center justify-center gap-1">
                  <span>Total Students</span>
                  {downloadMode && (
                    <DownloadIconButton
                      title={`Download all students in ${course.courseShortName || course.courseName || 'this course'}`}
                      onClick={() => exportCourseColumn(null)}
                    />
                  )}
                </div>
              </th>
              {STUDENT_STATUS_OPTIONS.map((opt) => (
                <th key={opt.value} className="border border-gray-400 px-2 py-1.5 text-center font-semibold text-gray-800">
                  <div className="flex items-center justify-center gap-1">
                    <span>{opt.label}</span>
                    {downloadMode && (
                      <DownloadIconButton
                        title={`Download all "${opt.label}" students in ${course.courseShortName || course.courseName || 'this course'}`}
                        onClick={() => exportCourseColumn(opt.value)}
                      />
                    )}
                  </div>
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {course.batches.map((batch) => {
              const counts = computeBatchStatusCounts(batch.students);
              const isEmpty = batch.students.length === 0;
              const batchLabel = batch.batchInternalName || batch.whatsappGroupName || batch.batchName || 'Batch';
              return (
                <tr key={batch.batchId} style={isEmpty ? { backgroundColor: '#fee2e2' } : undefined}>
                  <td className="border border-gray-400 px-2 py-1.5 font-semibold text-gray-800">{batchLabel}</td>
                  <td className="border border-gray-400 px-2 py-1.5 text-center font-semibold">
                    <div className="flex items-center justify-center gap-1">
                      <span>{batch.students.length}</span>
                      {downloadMode && (
                        <DownloadIconButton title={`Download all students in ${batchLabel}`} onClick={() => exportBatchCell(batch, null)} />
                      )}
                    </div>
                  </td>
                  {STUDENT_STATUS_OPTIONS.map((opt) => (
                    <td key={opt.value} className="border border-gray-400 px-2 py-1.5 text-center">
                      <div className="flex items-center justify-center gap-1">
                        <span>{counts[opt.value] || 0}</span>
                        {downloadMode && (
                          <DownloadIconButton
                            title={`Download "${opt.label}" students in ${batchLabel}`}
                            onClick={() => exportBatchCell(batch, opt.value)}
                          />
                        )}
                      </div>
                    </td>
                  ))}
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}
